//
// Copyright (c) 2026 Comcast
//

import { api } from './api'
import { ticketingSystemTicketType } from './tickets'

const enhancedApi = api.enhanceEndpoints({
  addTagTypes: [
    'ticketProviderTicket',
    'ticketProviderTicketAttachments',
    'ticketProviderTicketComments',
  ],
})

const getPartnerStatusesMap = (response) => {
  const partnerStatusesMap = {}
  const partnerStatuses = response?.partnerStatuses || []
  partnerStatuses.forEach(status => {
    partnerStatusesMap[status.name] = { color: status.color }
  })

  return partnerStatusesMap
}

// manually update ticket cache to prevent additional API fetch request
const manuallyUpdateTicketCache = async function ({ ticketProviderId, ticketId }, { dispatch, queryFulfilled }) {
  try {
    const { data } = await queryFulfilled

    if (data.error) {
      return
    }

    dispatch(
      api.util.updateQueryData(
        'fetchTicketProviderTicket',
        { ticketProviderId, ticketId },
        () => data
      )
    )
  } catch {}
}

const extendedApi = enhancedApi.injectEndpoints({
  endpoints: (builder) => ({

    // TICKETS

    fetchTicketProviderTickets: builder.query({
      query: (params) => {
        return ({
          url: `/admin/ticket_providers/${params.ticketProviderId}/tickets`,
          params: {
            ...(params.searchValue && { search: params.searchValue }),
            ...(params.statusFilters && { statusFilters: params.statusFilters }),
            ...(params.userFilters && { userFilters: params.userFilters }),
            ...(params.sortField && { sortField: params.sortField }),
            ...(params.sortDir && { sortDir: params.sortDir }),
            ...(params.providerTicketBoardId && { providerTicketBoardId: params.providerTicketBoardId }),
            page: params.page,
          },
        })
      },
      transformResponse: (response) => {
        response['partnerStatusesMap'] = getPartnerStatusesMap(response)
        return response
      },
      providesTags: (result, error, args) => {
        const tags = [{ type: 'ticketProviderTicket', id: 'LIST' }]
        // add tags for each ticket filter so it is easy to invalidate them on filter change
        if (args.userFilters) {
          args.userFilters.forEach(filterId => tags.push({
            type: 'ticketProviderTicket',
            id: `TICKET_FILTER_ID_${filterId}`,
          }))
        }
        return tags
      },
    }),

    reloadTicketProviderTickets: builder.mutation({
      query: ({ ticketProviderId }) => {
        return ({
          url: `/admin/ticket_providers/${ticketProviderId}/reload_tickets`,
          method: 'GET',
        })
      },
      invalidatesTags: [{ type: 'ticketProviderTicket', id: 'LIST' }],
    }),

    deleteTicketProviderTickets: builder.mutation({
      query: ({ ticketProviderId, ticketIds }) => {
        return ({
          url: `/admin/ticket_providers/${ticketProviderId}/tickets/destroy`,
          method: 'DELETE',
          body: { ticketIds: ticketIds },
        })
      },
      invalidatesTags: [
        { type: 'ticketProviderTicket', id: 'LIST' },
        { type: ticketingSystemTicketType, id: 'LIST' },
      ],
    }),

    // TICKET VIEW

    quickSearchProviderTickets: builder.query({
      query: ({ ticketProviderId, ...params }) => {
        return ({
          url: `/admin/ticket_providers/${ticketProviderId}/tickets/quick_search`,
          params: {
            ...(params.perPage && { perPage: params.perPage }),
            searchTerm: params.searchTerm,
          },
        })
      },
    }),

    fetchTicketProviderTicket: builder.query({
      query: ({ ticketProviderId, ticketId }) => {
        return ({
          url: `/admin/ticket_providers/${ticketProviderId}/tickets/${ticketId}`,
        })
      },
      providesTags: (result) => [{ type: 'ticketProviderTicket', id: 'CURRENT' }],
    }),

    // reuses tickets endpoint with different parameters and post-processing
    fetchTicketProviderTicketChildren: builder.query({
      query: ({ ticketProviderId, parentTicketId, ...params }) => {
        return ({
          url: `/admin/ticket_providers/${ticketProviderId}/tickets`,
          params: {
            ...(params.searchValue && { search: params.searchValue }),
            ...(params.statusFilters && { statusFilters: params.statusFilters }),
            ...(params.sortField && { sortField: params.sortField }),
            ...(params.sortDir && { sortDir: params.sortDir }),
            ...(params.perPage && { perPage: params.perPage }),
            parentTicketId: parentTicketId,
            page: params.page,
          },
        })
      },
      transformResponse: (response) => {
        response['partnerStatusesMap'] = getPartnerStatusesMap(response)
        return response
      },
      providesTags: (result) => [{ type: 'ticketProviderTicketChildren', id: 'LIST' }],
    }),

    fetchTicketProviderTicketAttachments: builder.query({
      query: (params) => {
        const { ticketProviderId, ticketId } = params
        return ({
          url: `/admin/ticket_providers/${ticketProviderId}/tickets/${ticketId}/attachments`,
          params: {
            ...(params.searchValue && { search: params.searchValue }),
            ...(params.partners && { partners: params.partners }),
            ...(params.extensions && { extensions: params.extensions }),
            ...(params.sortField && { sortField: params.sortField }),
            ...(params.sortDir && { sortDir: params.sortDir }),
            ...(params.perPage && { perPage: params.perPage }),
            ...(params.attachmentId && { attachmentId: params.attachmentId }),
            page: params.page,
          },
        })
      },
      providesTags: (result) => [{ type: 'ticketProviderTicketAttachments', id: 'LIST' }],
    }),

    fetchTicketProviderTicketComments: builder.query({
      query: (params) => {
        const { ticketProviderId, ticketId } = params
        return ({
          url: `/admin/ticket_providers/${ticketProviderId}/tickets/${ticketId}/comments`,
          params: {
            ...(params.searchValue && { search: params.searchValue }),
            ...(params.partners && { partners: params.partners }),
            ...(params.authors && { authors: params.authors }),
            ...(params.sortDir && { sortDir: params.sortDir }),
            ...(params.perPage && { perPage: params.perPage }),
            ...(params.commentId && { commentId: params.commentId }),
            page: params.page,
          },
        })
      },
      providesTags: (result) => [{ type: 'ticketProviderTicketComments', id: 'LIST' }],
    }),

    reloadTicketProviderTicket: builder.mutation({
      query: ({ ticketProviderId, ticketId }) => {
        return ({
          url: `/admin/ticket_providers/${ticketProviderId}/tickets/${ticketId}/reload_ticket`,
          method: 'GET',
        })
      },
      onQueryStarted: manuallyUpdateTicketCache,
      invalidatesTags: [
        { type: 'ticketProviderTicketChildren', id: 'LIST' },
        { type: 'ticketProviderTicketAttachments', id: 'LIST' },
        { type: 'ticketProviderTicketComments', id: 'LIST' },
      ],
    }),
  }),
  overrideExisting: false,
})

export const {
  useFetchTicketProviderTicketsQuery,
  useLazyFetchTicketProviderTicketsQuery,
  useReloadTicketProviderTicketsMutation,
  useDeleteTicketProviderTicketsMutation,
  useFetchTicketProviderTicketQuery,
  useFetchTicketProviderTicketChildrenQuery,
  useFetchTicketProviderTicketAttachmentsQuery,
  useFetchTicketProviderTicketCommentsQuery,
  useReloadTicketProviderTicketMutation,
} = extendedApi

export const {
  deleteTicketProviderTickets,
  reloadTicketProviderTicket,
  quickSearchProviderTickets,
  fetchTicketProviderTickets,
} = extendedApi.endpoints
